import {
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import CustomTextInput from "../../../../../global/components/CustomTextInput/CustomTextInput";
import { isTruthy, openSuccessNotification } from "helpers/methods";
import { cloneDeep } from "lodash";
import { CampaignContext } from "../../Context";
import {
  deliveryOptions,
  resultsAfterType,
  variables,
  winningMetric,
} from "../Constant";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import CustomLoader from "global/components/CustomLoader/CustomLoader";

interface CustomProps {
  setSkipStepOne: Function;
}

const Step1 = (props: CustomProps) => {
  const { submitCampaign, setSubmitCampaign, saveAsDraft } = useContext(CampaignContext);
  const [saving, setSaving] = useState<boolean>(false);
  const [formFields, setFormFields] = useState({
    name: {
      value: submitCampaign.campaign.name ?? "",
      error: "",
    },
    variable: {
      value: submitCampaign.abTestDetails?.testingVariable ?? "",
      error: "",
    },
    variant: {
      value: submitCampaign.abTestDetails?.testingVariants ?? 0,
      error: "",
    },
    winningMetric: {
      value: submitCampaign.abTestDetails?.winningMetric ?? "",
      error: "",
    },
    winningExecutionType: {
      value: submitCampaign.abTestDetails?.winningExecutionType ?? "",
      error: "",
    },
    resultsAfterInterval: {
      value: submitCampaign.abTestDetails?.resultsAfterInterval ?? "",
      error: "",
    },
    resultsAfterType: {
      value: submitCampaign.abTestDetails?.resultsAfterType ?? "",
      error: "",
    },
  });

  const startABCampaign = async () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    if (!isTruthy(clonedFormFields.name.value)) {
      error = true;
      clonedFormFields.name.error = "Please enter the campaign name";
    }
    if (!isTruthy(clonedFormFields.variable.value)) {
      error = true;
      clonedFormFields.variable.error = "Please select the variable to test";
    }
    if (clonedFormFields.variant.value === 0) {
      error = true;
      clonedFormFields.variant.error = "Please select number of the versions";
    }
    if (!isTruthy(clonedFormFields.winningMetric.value)) {
      error = true;
      clonedFormFields.winningMetric.error =
        "Please select how should we choose the winning version";
    }
    if (
      !isTruthy(clonedFormFields.resultsAfterType.value) ||
      !isTruthy(clonedFormFields.resultsAfterInterval.value)
    ) {
      error = true;
      clonedFormFields.resultsAfterType.error =
        "Please select how long should we run the test";
      clonedFormFields.resultsAfterInterval.error =
        "Please select how long should we run the test";
    }
    if (!isTruthy(clonedFormFields.winningExecutionType.value)) {
      error = true;
      clonedFormFields.winningExecutionType.error =
        "Please select how should we send the winning version";
    }
    if (error) {
      return setFormFields(clonedFormFields);
    }
    setSaving(true);
    const campaignObject = {
      ...submitCampaign,
      campaign: {
        ...submitCampaign.campaign,
        name: formFields.name.value.trim(),
      }, abTestDetails: {
        ...submitCampaign.abTestDetails,
        testingValues: submitCampaign.abTestDetails?.testingValues ?? [],
        testingVariable: formFields.variable.value,
        testingVariants: +formFields.variant.value,
        winningMetric: formFields.winningMetric.value,
        winningExecutionType: formFields.winningExecutionType.value,
        resultsAfterType: formFields.resultsAfterType.value,
        resultsAfterInterval: formFields.resultsAfterInterval.value,
      },
    };
    setSubmitCampaign(campaignObject);
    const campaignId = await saveAsDraft(campaignObject);
    history.push(
      viewpaths.createABTestCampaignViewPath + `?id=${campaignId}`
    );
    props.setSkipStepOne(true);
    setSaving(false);
  };

  const getIntervalOptions = () => {
    if (formFields.resultsAfterType.value === "Hours") {
      return Array.from({ length: 23 }, (_, i) => i + 1);
    } else if (formFields.resultsAfterType.value === "Days") {
      return Array.from({ length: 7 }, (_, i) => i + 1);
    }
    return [];
  };

  const getPage = () => {
    return (
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Typography variant="h4">{`${submitCampaign.campaign.id !== 0 ? "Update" : "Create"}`} AB test campaign</Typography>
          <Typography variant="body1">
            Begin your A/B test campaign by providing a name, picking a testing
            variable, and setting the number of versions.
          </Typography>
        </Stack>
        <CustomTextInput
          label="Campaign name"
          value={formFields.name.value}
          onChange={(event: any) => {
            setFormFields({
              ...formFields,
              name: {
                value: event.target.value,
                error: "",
              },
            });
          }}
          error={formFields.name.error}
          required
          placeHolder="Enter the campaign name"
          propsToInputElement={{ minLength: 1, maxLength: 90 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <span style={{ fontSize: "0.75rem" }}>
                  {formFields.name?.value?.length} / 90
                </span>
              </InputAdornment>
            ),
          }}
        />
        <Stack spacing={0.5}>
          <InputLabel required>Choose the variable</InputLabel>
          <Typography variant="body2" color="#afb4bc" fontSize="13px">
            Select the variable you wish to test, and we’ll generate campaigns
            based on each of its versions.
          </Typography>
          <RadioGroup
            value={formFields.variable.value}
            onChange={(event: any) => {
              setFormFields({
                ...formFields,
                variable: {
                  value: event.target.value,
                  error: "",
                },
              });
              setSubmitCampaign({
                ...submitCampaign,
                abTestDetails: {
                  ...submitCampaign.abTestDetails,
                  testingValues: [],
                },
              });
            }}
          >
            <Grid container>
              {Object.values(variables).map((variable) => (
                <Grid item xs={6}>
                  <FormControlLabel
                    value={variable.value}
                    control={<Radio size="small" />}
                    label={variable.label}
                  />
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
          {isTruthy(formFields.variable.error) && (
            <FormHelperText error>{formFields.variable.error}</FormHelperText>
          )}
        </Stack>
        <Stack spacing={0.5}>
          <InputLabel required>Choose the number of versions</InputLabel>
          <Typography variant="body2" color="#afb4bc" fontSize="13px">
            Based on your selection, we’ll generate the specified number of
            versions.
          </Typography>
          <RadioGroup
            value={formFields.variant.value}
            onChange={(event: any) => {
              setFormFields({
                ...formFields,
                variant: {
                  value: event.target.value,
                  error: "",
                },
              });
            }}
          >
            <Grid container>
              <Grid item xs={6}>
                <FormControlLabel
                  value={2}
                  control={<Radio size="small" />}
                  label="Two versions"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  value={3}
                  control={<Radio size="small" />}
                  label="Three versions"
                />
              </Grid>
            </Grid>
          </RadioGroup>
          {isTruthy(formFields.variant.error) && (
            <FormHelperText error>{formFields.variant.error}</FormHelperText>
          )}
        </Stack>
        <Stack spacing={0.5}>
          <InputLabel required>
            How will the winning version to be chosen?
          </InputLabel>
          <Typography variant="body2" color="#afb4bc" fontSize="13px">
            Choose the metrics that will determinate the winning version.
          </Typography>
          <RadioGroup
            value={formFields.winningMetric.value}
            onChange={(event: any) => {
              setFormFields({
                ...formFields,
                winningMetric: {
                  value: event.target.value,
                  error: "",
                },
              });
            }}
          >
            {Object.values(winningMetric).map((metric) => (
              <FormControlLabel
                value={metric.value}
                control={<Radio size="small" />}
                label={metric.label}
              />
            ))}
          </RadioGroup>
          {isTruthy(formFields.winningMetric.error) && (
            <FormHelperText error>
              {formFields.winningMetric.error}
            </FormHelperText>
          )}
        </Stack>
        <Stack spacing={0.5}>
          <InputLabel required>How long should the test run?</InputLabel>
          <Typography variant="body2" color="#afb4bc" fontSize="13px">
            Choose the duration of the test.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Select
              size="small"
              sx={{ width: "200px" }}
              error={isTruthy(formFields.resultsAfterType.error)}
              value={formFields.resultsAfterType.value}
              onChange={(event: any) => {
                setFormFields({
                  ...formFields,
                  resultsAfterType: {
                    value: event.target.value,
                    error: "",
                  },
                });
              }}
              renderValue={
                formFields.resultsAfterType.value !== ""
                  ? undefined
                  : () => (
                    <Typography variant="caption" color="text.secondary">
                      Select test duration
                    </Typography>
                  )
              }
              displayEmpty
            >
              {resultsAfterType.map((option) => (
                <MenuItem value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              sx={{ width: "200px" }}
              error={isTruthy(formFields.resultsAfterInterval.error)}
              value={formFields.resultsAfterInterval.value}
              onChange={(event: any) => {
                setFormFields({
                  ...formFields,
                  resultsAfterInterval: {
                    value: event.target.value,
                    error: "",
                  },
                });
              }}
              renderValue={
                formFields.resultsAfterInterval.value !== 0
                  ? undefined
                  : () => (
                    <Typography variant="caption" color="text.secondary">
                      Select interval
                    </Typography>
                  )
              }
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    overflowY: "auto",
                  },
                },
              }}
            >
              {getIntervalOptions().map((option) => (
                <MenuItem value={option}>{option}</MenuItem>
              ))}
            </Select>
          </Stack>
          {isTruthy(formFields.resultsAfterType.error) && (
            <FormHelperText error>
              {formFields.resultsAfterType.error}
            </FormHelperText>
          )}
        </Stack>
        <Stack spacing={0.5}>
          <InputLabel required>How do we send the winning version?</InputLabel>
          <Typography variant="body2" color="#afb4bc" fontSize="13px">
            The winning version will be sent based on your chosen delivery
            method. You can update this at any time before the test is complete.
          </Typography>
          <Box>
            <Select
              size="small"
              sx={{ width: "200px" }}
              placeholder="How do we send the winning version?"
              error={isTruthy(formFields.winningExecutionType.error)}
              value={formFields.winningExecutionType.value}
              onChange={(event: any) => {
                setFormFields({
                  ...formFields,
                  winningExecutionType: {
                    value: event.target.value,
                    error: "",
                  },
                });
              }}
              renderValue={
                formFields.winningExecutionType.value !== ""
                  ? undefined
                  : () => (
                    <Typography variant="caption" color="text.secondary">
                      Select execution type
                    </Typography>
                  )
              }
              displayEmpty
            >
              {Object.values(deliveryOptions).map((option) => (
                <MenuItem value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </Box>
          {isTruthy(formFields.winningExecutionType.error) && (
            <FormHelperText error>
              {formFields.winningExecutionType.error}
            </FormHelperText>
          )}
        </Stack>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button
            variant="text"
            // onClick={() => history.push(viewpaths.campaignsViewPath)}
            onClick={() =>
              history.push(
                `${viewpaths.campaignsViewPath
                }?page=${1}&rowsPerPage=${10}&status=All&category=all&currentTab=email`
              )
            }
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={startABCampaign}>
            {`${submitCampaign.campaign.id !== 0 ? "Update" : "Create"}`} A/B test campaign
          </Button>
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      {getPage()}
      <CustomLoader isLoading={saving} />
    </>
  );
};

export default Step1;
